import noUiSlider from "nouislider";
import DropdownSelector from "~/js/common/dropdownSelector";

/**
 * 検索パネル（スライドイン）
 * ヘッダーの虫眼鏡アイコンから全ページで開閉可能。
 * Section Rendering APIで0件チェック後に検索結果ページへ遷移する。
 */
class SearchPanel {
  constructor() {
    this.panel = document.getElementById("search-panel");
    if (!this.panel) return;

    this.container = this.panel.querySelector(".search-panel__container");
    this.body = this.panel.querySelector(".search-panel__body");
    this.form = this.panel.querySelector('form[name="search-panel-form"]');
    this.messageEl = document.getElementById("search-panel-message");
    this.submitBtn = this.panel.querySelector(".js-sp-submit");
    this.priceSlider = null;
    this.priceSliderConfig = null;
    this.sliderInitialized = false;
    this.dropdown = null;
    // auto時(全閉時)の body 実スクロールバー幅。Windows等で>0、Macオーバーレイは0
    this.bodyScrollbarWidth = 0;

    this.setupOpenButtons();
    this.setupCloseButtons();
    this.setupEscKey();
    this.setupClear();
    this.setupSubmit();
    this.listenOpenEvent();
  }

  // =========================================
  // スクロールバー幅補正（横ズレ対策）
  // =========================================

  /**
   * ページのスクロール固定（開く時）。
   * body を overflow:hidden にするとページの縦スクロールバーが消え、
   * その幅ぶんページ全体が横にズレるため padding-right で補填する。
   */
  lockPageScroll() {
    var sbw = window.innerWidth - document.documentElement.clientWidth;
    if (sbw > 0) {
      document.body.style.paddingRight = sbw + "px";
    }
    // 固定ヘッダー(.l-header position:fixed)はビューポート基準で body の padding-right
    // 補正が効かないため、CSS側で width を sbw ぶん縮める。その幅を変数で渡す（CSS参照）。
    // ※ JSとCSS(searchPanel.scss の width:calc)は必ずセットで反映すること。
    //   JSが未反映だと --sp-sbw が無く calc(100% - 0px)=100% で補正が無効になる。
    document.documentElement.style.setProperty("--sp-sbw", sbw + "px");
    document.body.classList.add("search-panel-open");
  }

  unlockPageScroll() {
    document.body.classList.remove("search-panel-open");
    document.body.style.paddingRight = "";
    document.documentElement.style.removeProperty("--sp-sbw");
  }

  /**
   * ドロップダウン開閉に伴う内側 body の横ズレ補正。
   * ドロップダウンを開くと CSS の :has() で body が overflow:visible になり、
   * 内側スクロールバーが消える。元々スクロールバーが出ていた場合のみ、
   * 消える幅を padding-right に上乗せしてフォームのズレを防ぐ。
   *
   * DropdownSelector の onDropdownToggle から、.active クラスのトグルと
   * 同一実行タイミング（同期）で呼ばれる。MutationObserver による後追い補正だと
   * :has() の overflow 切替と別レイアウトになり、Win実機で1フレーム横ズレが残るため。
   */
  updateInnerCompensation() {
    if (!this.body) return;
    var anyActive = !!this.panel.querySelector(".search-panel__options.active");
    if (!anyActive) {
      // 全閉時は overflow:auto。この時の実スクロールバー幅(0 or 15等)をキャッシュ。
      // 開くと :has() で overflow:visible になりスクロールバーが消えて測れなくなるため。
      this.bodyScrollbarWidth = this.body.offsetWidth - this.body.clientWidth;
      this.setBodyCompensation(0);
      return;
    }
    // 閉じている時にスクロールバーが出ていた幅ぶんだけ補填（出ていなければ0）
    this.setBodyCompensation(this.bodyScrollbarWidth);
  }

  setBodyCompensation(px) {
    // いったんレスポンシブの基準 padding に戻してから上乗せする
    this.body.style.paddingRight = "";
    if (px > 0) {
      var base = parseFloat(getComputedStyle(this.body).paddingRight) || 0;
      this.body.style.paddingRight = base + px + "px";
    }
  }

  // =========================================
  // パネル開閉
  // =========================================

  open(params) {
    // ページ縦スクロールバーが消えることによる横ズレを補填してから開く
    this.lockPageScroll();
    this.panel.classList.add("is-open");
    this.panel.setAttribute("aria-hidden", "false");

    // ドロップダウン + スライダー初期化（初回のみ）
    if (!this.sliderInitialized) {
      var self = this;
      this.dropdown = new DropdownSelector(this.panel, {
        prefix: "sp-",
        onDropdownToggle: function () {
          self.updateInnerCompensation();
        },
      });
      this.initPriceSlider();
      this.sliderInitialized = true;
    }

    // パラメータ復元
    if (params) {
      this.restoreParams(params);
    }

    // メッセージクリア
    this.hideMessage();
    this.hideLoading();

    // 全閉(auto)時の body 実スクロールバー幅をキャッシュ（内側横ズレ補正に使用）
    if (this.body) {
      this.bodyScrollbarWidth = this.body.offsetWidth - this.body.clientWidth;
    }
  }

  close() {
    this.panel.classList.remove("is-open");
    this.panel.setAttribute("aria-hidden", "true");
    this.unlockPageScroll();
    if (this.dropdown) {
      this.dropdown.closeAllDropdowns();
    }
    this.setBodyCompensation(0);
  }

  // =========================================
  // イベント設定
  // =========================================

  setupOpenButtons() {
    var self = this;
    var btns = document.querySelectorAll(".js-search-panel-open");
    btns.forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        var params = self.getSearchPageParams();
        self.open(params);
      });
    });
  }

  setupCloseButtons() {
    var self = this;
    var btns = this.panel.querySelectorAll(".js-search-panel-close");
    btns.forEach(function (btn) {
      btn.addEventListener("click", function (e) {
        e.preventDefault();
        self.close();
      });
    });
  }

  setupEscKey() {
    var self = this;
    document.addEventListener("keydown", function (e) {
      if (e.key === "Escape" && self.panel.classList.contains("is-open")) {
        self.close();
      }
    });
  }

  listenOpenEvent() {
    var self = this;
    document.addEventListener("open-search-panel", function (e) {
      var params = e.detail || self.getSearchPageParams();
      self.open(params);
    });
  }

  // =========================================
  // 検索ページパラメータ取得
  // =========================================

  getSearchPageParams() {
    var url = new URL(window.location.href);
    var p = url.searchParams;

    var keyword = p.get("keyword") || p.get("q") || "";
    var brand = p.get("brand") || p.getAll("filter.p.vendor").join(",") || "";
    var item = p.get("item") || p.getAll("filter.p.m.im-ft.item_category").join(",") || "";
    var color = p.get("color") || p.getAll("filter.v.m.im-ft.vcolor").join(",") || "";
    var price1 = p.get("price_1") || p.get("filter.v.price.gte") || "";
    var price2 = p.get("price_2") || p.get("filter.v.price.lte") || "";

    if (!keyword && !brand && !item && !color && !price1 && !price2) {
      return null;
    }

    return { keyword: keyword, brand: brand, item: item, color: color, price1: price1, price2: price2 };
  }

  // =========================================
  // パラメータ復元
  // =========================================

  restoreParams(params) {
    if (!params) return;

    // キーワード
    if (params.keyword && params.keyword !== "*") {
      this.form.querySelector('input[name="q"]').value = params.keyword;
    }

    // ブランド
    if (params.brand) {
      this.setDropdownValues(".js-sp-brand-option", params.brand);
    }

    // アイテム
    if (params.item) {
      this.setDropdownValues(".js-sp-item-option", params.item);
    }

    // カラー
    if (params.color) {
      this.setDropdownValues(".js-sp-color-option", params.color);
    }

    // 価格
    if (params.price1 || params.price2) {
      var min = params.price1 ? parseInt(params.price1, 10) : null;
      var max = params.price2 ? parseInt(params.price2, 10) : null;
      if (this.priceSlider) {
        var currentValues = this.priceSlider.get();
        this.priceSlider.set([
          min !== null && !isNaN(min) ? min : currentValues[0],
          max !== null && !isNaN(max) ? max : currentValues[1]
        ]);
      }
    }
  }

  /**
   * ドロップダウンに値を設定する
   */
  setDropdownValues(targetSelector, valuesStr) {
    var wrapper = this.panel.querySelector(targetSelector);
    if (!wrapper) return;

    var values = valuesStr.split(",").map(function (v) { return v.trim(); }).filter(Boolean);
    if (values.length === 0) return;

    // デフォルト選択を解除
    var defaultOpt = wrapper.querySelector(".js-sp-default");
    if (defaultOpt) {
      defaultOpt.classList.remove("active");
      defaultOpt.setAttribute("aria-pressed", "false");
    }

    // 各値に対応するオプションをactive化
    var options = wrapper.querySelectorAll(".js-sp-option");
    options.forEach(function (opt) {
      var optValue = opt.getAttribute("data-value");
      if (values.indexOf(optValue) !== -1) {
        opt.classList.add("active");
        opt.setAttribute("aria-pressed", "true");

        // 親カテゴリを開く
        var category = opt.closest("[class*='js-sp-category-']");
        if (category) {
          category.classList.add("open");
          var catTitle = category.querySelector(".js-sp-option-category");
          if (catTitle) catTitle.setAttribute("aria-expanded", "true");
        }
      }
    });

    // ラベルとhidden inputは共通ロジックで再構築（色玉含む）
    if (this.dropdown) this.dropdown.updateDisplay(wrapper);
  }

  // =========================================
  // 価格スライダー
  // =========================================

  initPriceSlider() {
    var sliderEl = this.panel.querySelector(".js-sp-price-slider");
    if (!sliderEl) return;

    var maxPriceFromSettings = parseInt(sliderEl.dataset.maxPrice, 10);
    var maxPrice = isNaN(maxPriceFromSettings) || maxPriceFromSettings <= 0 ? 500000 : maxPriceFromSettings;
    var CONFIG = {
      step: 1000,
      min: 0,
      max: maxPrice,
      defaultMin: 0,
      defaultMax: maxPrice,
    };

    this.priceSliderConfig = CONFIG;

    this.priceSlider = noUiSlider.create(sliderEl, {
      start: [CONFIG.defaultMin, CONFIG.defaultMax],
      connect: true,
      step: CONFIG.step,
      range: {
        min: CONFIG.min,
        max: CONFIG.max,
      },
      format: {
        to: function (value) { return Math.round(value); },
        from: function (value) { return Number(value); },
      },
    });

    var panel = this.panel;
    this.priceSlider.on("update", function (values) {
      var minVal = Number(values[0]);
      var maxVal = Number(values[1]);
      var minDisplay = panel.querySelector(".js-sp-min-price");
      var maxDisplay = panel.querySelector(".js-sp-max-price");
      var minInput = panel.querySelector(".js-sp-price-min-input");
      var maxInput = panel.querySelector(".js-sp-price-max-input");
      if (minDisplay) minDisplay.textContent = minVal.toLocaleString("ja-JP");
      if (maxDisplay) maxDisplay.textContent = maxVal.toLocaleString("ja-JP");
      if (minInput) minInput.value = minVal;
      if (maxInput) maxInput.value = maxVal;
    });
  }

  // =========================================
  // クリア
  // =========================================

  setupClear() {
    var self = this;
    var clearBtn = this.panel.querySelector(".js-sp-clear");
    if (!clearBtn) return;

    clearBtn.addEventListener("click", function () {
      // キーワードクリア
      var keywordInput = self.form.querySelector('input[name="q"]');
      if (keywordInput) keywordInput.value = "";

      // 全ドロップダウンをリセット（共通クラスに委譲）
      if (self.dropdown) {
        self.dropdown.clearAllSelections();
      }

      // 価格スライダーリセット
      if (self.priceSlider && self.priceSliderConfig) {
        self.priceSlider.set([self.priceSliderConfig.defaultMin, self.priceSliderConfig.defaultMax]);
      }

      // メッセージクリア
      self.hideMessage();
    });
  }

  // =========================================
  // フォーム送信（0件チェック）
  // =========================================

  setupSubmit() {
    var self = this;
    var submitBtn = this.panel.querySelector(".js-sp-submit");
    if (!submitBtn) return;

    submitBtn.addEventListener("click", function () {
      self.handleSubmit();
    });
  }

  handleSubmit() {
    var self = this;
    this.hideMessage();
    this.showLoading();

    var params = this.buildSearchParams();
    var searchUrl = "/search?type=product&" + params.toString();

    // Section Rendering APIで0件チェック
    var sraUrl = searchUrl + "&section_id=main-search";

    fetch(sraUrl)
      .then(function (response) {
        if (!response.ok) throw new Error("Network error");
        return response.text();
      })
      .then(function (html) {
        self.hideLoading();

        var parser = new DOMParser();
        var doc = parser.parseFromString(html, "text/html");
        var searchSection = doc.querySelector("[data-search-results]");
        var resultsCount = searchSection ? parseInt(searchSection.getAttribute("data-search-results"), 10) : -1;

        if (resultsCount === 0) {
          self.showMessage("検索条件に該当する商品はありません。");
        } else {
          window.location.href = searchUrl;
        }
      })
      .catch(function () {
        self.hideLoading();
        self.showMessage("エラーが発生しました。もう一度お試しください。");
      });
  }

  buildSearchParams() {
    var params = new URLSearchParams();
    var formData = new FormData(this.form);

    var keyword = (formData.get("q") || "").trim();
    params.set("q", keyword || "*");

    params.set("filter.p.product_type", "normal");

    // ドロップダウン値（カンマ区切り対応）
    var multiFields = ["filter.p.vendor", "filter.p.m.im-ft.item_category", "filter.v.m.im-ft.vcolor"];
    multiFields.forEach(function (fieldName) {
      var val = formData.get(fieldName);
      if (val) {
        val.split(",").forEach(function (v) {
          if (v.trim()) params.append(fieldName, v.trim());
        });
      }
    });

    // 価格
    var priceMin = formData.get("filter.v.price.gte");
    var priceMax = formData.get("filter.v.price.lte");
    if (priceMin) params.set("filter.v.price.gte", priceMin);
    if (priceMax) params.set("filter.v.price.lte", priceMax);

    return params;
  }

  // =========================================
  // メッセージ / ローディング
  // =========================================

  showMessage(text) {
    if (this.messageEl) {
      this.messageEl.textContent = text;
      this.messageEl.style.display = "block";
    }
  }

  hideMessage() {
    if (this.messageEl) {
      this.messageEl.textContent = "";
      this.messageEl.style.display = "none";
    }
  }

  showLoading() {
    if (this.submitBtn) {
      this.submitBtn.classList.add("is-loading");
      this.submitBtn.disabled = true;
    }
  }

  hideLoading() {
    if (this.submitBtn) {
      this.submitBtn.classList.remove("is-loading");
      this.submitBtn.disabled = false;
    }
  }
}

// 初期化
document.addEventListener("DOMContentLoaded", function () {
  new SearchPanel();
});
